import React, { useState, useEffect, useRef } from 'react';
import { formatDate } from '../utils/dateUtils';
import { Link } from 'react-router-dom';
import {
  DocumentMagnifyingGlassIcon,
  PlayIcon,
  EyeIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  MinusCircleIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../components/LoadingSpinner';
import TGChibi, { TGChibiVariants } from '../components/TGChibi';
import api from '../services/api';

export default function Scans() {
  const [scans, setScans] = useState([]);
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState('');
  const [creatingScans, setCreatingScan] = useState(false);
  const [error, setError] = useState('');
  const [activeLogMenu, setActiveLogMenu] = useState(null);

  // New state for bulk actions and folding
  const [selectedScans, setSelectedScans] = useState(new Set());
  const [expandedSections, setExpandedSections] = useState({
    recent: true,
    older: false,
    archive: false
  });



  const currentScansRef = useRef(scans);
  const dropdownRef = useRef(null);

  // Handle clicks outside the dropdown menu
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveLogMenu(null);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    fetchData();

    // Set up auto-refresh every 10 seconds to show scan status updates
    const interval = setInterval(() => {
      fetchData();
    }, 10000);

    // Cleanup interval on component unmount
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    currentScansRef.current = scans;
  }, [scans]);

  // Separate effect for polling running scans
  useEffect(() => {
    const pollInterval = setInterval(() => {
      const runningScans = currentScansRef.current.filter(scan => scan.status === 'running');

      // Only poll if there are running scans
      if (runningScans.length > 0) {
        fetchData();
      }
    }, 5000); // Poll every 5 seconds

    return () => {
      clearInterval(pollInterval);
    };
  }, []); // Run once on mount

  const fetchData = async () => {
    try {
      const [scansResponse, domainsResponse] = await Promise.all([
        api.get('/scans/'),
        api.get('/domains/')
      ]);

      console.log('Scans response:', scansResponse.data);
      console.log('Domains response:', domainsResponse.data);

      setScans(scansResponse.data || []);
      // Show all active domains for scanning
      const allDomains = domainsResponse.data?.filter(d => d.is_active === true) || [];
      setDomains(allDomains);
    } catch (error) {
      console.error('Error fetching data:', error);
      console.error('Error response:', error.response?.data);

      // Clear any potentially problematic data
      setScans([]);
      setDomains([]);

      let errorMessage = 'Failed to load data';

      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (Array.isArray(error.response.data)) {
          // Handle validation errors array
          errorMessage = error.response.data.map(err =>
            typeof err === 'string' ? err : err.msg || JSON.stringify(err)
          ).join(', ');
        } else {
          errorMessage = JSON.stringify(error.response.data);
        }
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateScan = async (e) => {
    e.preventDefault();
    setCreatingScan(true);
    setError('');

    try {
      await api.post('/scans', {
        domain_id: parseInt(selectedDomain),
        scan_type: 'typosquatting'
      });

      setSelectedDomain('');
      setShowCreateForm(false);
      fetchData();
    } catch (error) {
      console.error('Error creating scan:', error);
      let errorMessage = 'Failed to create scan';

      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (Array.isArray(error.response.data)) {
          // Handle validation errors array
          errorMessage = error.response.data.map(err =>
            typeof err === 'string' ? err : err.msg || JSON.stringify(err)
          ).join(', ');
        } else {
          errorMessage = JSON.stringify(error.response.data);
        }
      }

      setError(errorMessage);
    } finally {
      setCreatingScan(false);
    }
  };

  const handleDeleteScan = async (scanId) => {
    if (!window.confirm('Are you sure you want to delete this scan? This action cannot be undone.')) {
      return;
    }

    try {
      await api.delete(`/scans/${scanId}`);
      fetchData(); // Refresh the scans list
    } catch (error) {
      console.error('Error deleting scan:', error);
      let errorMessage = 'Failed to delete scan';

      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (Array.isArray(error.response.data)) {
          // Handle validation errors array
          errorMessage = error.response.data.map(err =>
            typeof err === 'string' ? err : err.msg || JSON.stringify(err)
          ).join(', ');
        } else {
          errorMessage = JSON.stringify(error.response.data);
        }
      }

      setError(errorMessage);
    }
  };

  const handleDownloadLogs = async (scanId, format = 'full') => {
    try {
      // Use responseType: 'blob' to handle binary data correctly
      const response = await api.get(`/scans/${scanId}/logs`, {
        responseType: 'blob',
        params: { format }
      });

      // Create a URL for the blob
      const url = window.URL.createObjectURL(new Blob([response.data]));

      // Extract filename from Content-Disposition header if available
      let filename = `scan-${scanId}-logs-${format}.json`;
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=([^;]+)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/"/g, '');
        }
      }

      // Create temporary link and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading logs:', error);

      // Get a user-friendly error message
      let errorMessage = 'Failed to download scan logs.';

      if (error.response?.status === 404 && error.response?.data?.detail) {
        // Use the detailed error message from the backend
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        // Use the error message if available
        errorMessage += ' ' + error.message;
      }

      setError(errorMessage);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'running':
        return <ClockIcon className="h-5 w-5 text-mint-500" />;
      case 'failed':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-green-600 bg-green-100 dark:bg-green-900/20 dark:text-green-300';
      case 'running':
        return 'text-mint-600 bg-mint-100 dark:bg-mint-900/20 dark:text-mint-300';
      case 'failed':
        return 'text-red-600 bg-red-100 dark:bg-red-900/20 dark:text-red-300';
      default:
        return 'text-gray-600 bg-gray-100 dark:bg-gray-900/20 dark:text-gray-300';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high':
        return 'text-red-600 bg-red-100 dark:bg-red-900/20 dark:text-red-300';
      case 'medium':
        return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/20 dark:text-yellow-300';
      case 'low':
        return 'text-green-600 bg-green-100 dark:bg-green-900/20 dark:text-green-300';
      case 'whitelisted':
        return 'text-blue-600 bg-blue-100 dark:bg-blue-900/20 dark:text-blue-300';
      default:
        return 'text-gray-600 bg-gray-100 dark:bg-gray-900/20 dark:text-gray-300';
    }
  };

  // --- Bulk Actions & Grouping Logic ---

  const toggleScanSelection = (scanId) => {
    const newSelected = new Set(selectedScans);
    if (newSelected.has(scanId)) {
      newSelected.delete(scanId);
    } else {
      newSelected.add(scanId);
    }
    setSelectedScans(newSelected);
  };

  const toggleSelectAll = (scansInGroup) => {
    const allSelected = scansInGroup.every(scan => selectedScans.has(scan.scan_id));
    const newSelected = new Set(selectedScans);

    if (allSelected) {
      scansInGroup.forEach(scan => newSelected.delete(scan.scan_id));
    } else {
      scansInGroup.forEach(scan => newSelected.add(scan.scan_id));
    }
    setSelectedScans(newSelected);
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete ${selectedScans.size} scans? This action cannot be undone.`)) {
      return;
    }

    try {
      // Create a local copy of IDs to delete
      const idsToDelete = Array.from(selectedScans);

      // Call the bulk delete endpoint
      // Note: We're sending 'data' in the config object for axios DELETE requests
      await api.delete('/scans/bulk', { data: idsToDelete });

      // Clear selection and refresh
      setSelectedScans(new Set());
      fetchData();
    } catch (error) {
      console.error('Error deleting scans:', error);
      setError('Failed to delete selected scans');
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const groupScans = (scans) => {
    const now = new Date();
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    const groups = {
      recent: [],
      older: [],
      archive: []
    };

    scans.forEach(scan => {
      const scanDate = new Date(scan.started_at);
      if (scanDate >= oneWeekAgo) {
        groups.recent.push(scan);
      } else if (scanDate >= oneMonthAgo) {
        groups.older.push(scan);
      } else {
        groups.archive.push(scan);
      }
    });

    return groups;
  };

  const groupedScans = groupScans(scans);

  // --- Render Components ---

  const BulkActionsBar = () => {
    if (selectedScans.size === 0) return null;

    return (
      <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-white dark:bg-dark-800 shadow-xl rounded-lg px-6 py-3 border border-gray-200 dark:border-dark-600 flex items-center space-x-6 z-50 animate-fade-in-up">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
          {selectedScans.size} selected
        </span>
        <div className="h-4 w-px bg-gray-300 dark:bg-dark-500"></div>
        <button
          onClick={handleBulkDelete}
          className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 text-sm font-medium flex items-center"
        >
          <TrashIcon className="h-4 w-4 mr-2" />
          Delete Selected
        </button>
        <button
          onClick={() => setSelectedScans(new Set())}
          className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-sm flex items-center"
        >
          <XCircleIcon className="h-4 w-4 mr-1" />
          Cancel
        </button>
      </div>
    );
  };

  const ScanTable = ({ scans, showHeader = false }) => (
    <div className="overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-600">
        {showHeader && (
          <thead className="bg-gray-50 dark:bg-dark-700">
            <tr>
              <th scope="col" className="px-6 py-3 text-left">
                <input
                  type="checkbox"
                  checked={scans.length > 0 && scans.every(s => selectedScans.has(s.scan_id))}
                  onChange={() => toggleSelectAll(scans)}
                  className="rounded border-gray-300 text-mint-600 focus:ring-mint-500 h-4 w-4"
                />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Domain
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Findings
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Started
              </th>
              <th className="relative px-6 py-3">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
        )}
        <tbody className="bg-white dark:bg-dark-800 divide-y divide-gray-200 dark:divide-dark-600">
          {scans.map((scan) => (
            <tr key={scan.scan_id} className={selectedScans.has(scan.scan_id) ? "bg-mint-50 dark:bg-mint-900/10" : ""}>
              <td className="px-6 py-4 whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={selectedScans.has(scan.scan_id)}
                  onChange={() => toggleScanSelection(scan.scan_id)}
                  className="rounded border-gray-300 text-mint-600 focus:ring-mint-500 h-4 w-4"
                />
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  <DocumentMagnifyingGlassIcon className="h-5 w-5 text-gray-400 mr-3" />
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    {scan.domain?.domain_name || 'Unknown'}
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  {getStatusIcon(scan.status)}
                  <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(scan.status)}`}>
                    {scan.status}
                  </span>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                {scan.scan_type}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                {scan.findings_count > 0 ? (
                  <div className="flex space-x-1">
                    {scan.high_risk_count > 0 && (
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor('high')}`}>
                        {scan.high_risk_count} high
                      </span>
                    )}
                    {scan.medium_risk_count > 0 && (
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor('medium')}`}>
                        {scan.medium_risk_count} medium
                      </span>
                    )}
                    {scan.low_risk_count > 0 && (
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor('low')}`}>
                        {scan.low_risk_count} low
                      </span>
                    )}
                    {scan.whitelisted_count > 0 && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-blue-600 bg-blue-100 dark:bg-blue-900/20 dark:text-blue-300">
                        {scan.whitelisted_count} whitelisted
                      </span>
                    )}
                  </div>
                ) : (
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {scan.status === 'completed' ? 'No findings' : '-'}
                  </span>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                {formatDate(scan.started_at)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <div className="flex items-center justify-end space-x-2">
                  {scan.status === 'completed' && (
                    <Link
                      to={`/reports/${scan.scan_id}`}
                      className="text-mint-600 hover:text-mint-900 dark:text-mint-400 dark:hover:text-mint-300"
                      title="View Report"
                    >
                      <EyeIcon className="h-5 w-5" />
                    </Link>
                  )}
                  <button
                    type="button"
                    onClick={() => handleDeleteScan(scan.scan_id)}
                    className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                    title="Delete Scan"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                  <div className="relative inline-block text-left" ref={activeLogMenu === scan.scan_id ? dropdownRef : null}>
                    <div>
                      <button
                        type="button"
                        onClick={() => setActiveLogMenu(activeLogMenu === scan.scan_id ? null : scan.scan_id)}
                        className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 flex items-center"
                        title="Download Logs"
                      >
                        <ArrowDownTrayIcon className="h-5 w-5" />
                      </button>
                    </div>
                    {activeLogMenu === scan.scan_id && (
                      <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white dark:bg-dark-700 ring-1 ring-black ring-opacity-5 z-10">
                        <div className="py-1" role="menu" aria-orientation="vertical">
                          <button
                            onClick={() => {
                              handleDownloadLogs(scan.scan_id, 'full');
                              setActiveLogMenu(null);
                            }}
                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-dark-600"
                            role="menuitem"
                          >
                            Full Logs
                          </button>
                          <button
                            onClick={() => {
                              handleDownloadLogs(scan.scan_id, 'summary');
                              setActiveLogMenu(null);
                            }}
                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-dark-600"
                            role="menuitem"
                          >
                            Summary Logs
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const AccordionSection = ({ title, scans, expanded, onToggle, count }) => (
    <div className="mb-4 bg-white dark:bg-dark-800 shadow-glow rounded-lg overflow-hidden border border-gray-200 dark:border-dark-600">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-6 py-4 bg-gray-50 dark:bg-dark-700 hover:bg-gray-100 dark:hover:bg-dark-600 transition-colors duration-150"
      >
        <div className="flex items-center space-x-3">
          {expanded ? (
            <ChevronUpIcon className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronDownIcon className="h-5 w-5 text-gray-400" />
          )}
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            {title}
          </h3>
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-dark-600 text-gray-800 dark:text-gray-200">
            {count}
          </span>
        </div>
        {!expanded && scans.length > 0 && (
          <div className="flex space-x-4 text-sm text-gray-500 dark:text-gray-400 hidden sm:flex">
            <span>{scans.length} scans</span>
            <span className="text-gray-300 dark:text-dark-500">|</span>
            <span>Latest: {formatDate(scans[0].started_at)}</span>
          </div>
        )}
      </button>

      {expanded && (
        <div className="border-t border-gray-200 dark:border-dark-600">
          {scans.length > 0 ? (
            <>
              <div className="hidden md:block">
                <ScanTable scans={scans} showHeader={true} />
              </div>
              <div className="md:hidden space-y-4 p-4">
                {scans.map(scan => (
                  <div key={scan.scan_id} className={`bg-white dark:bg-dark-800 rounded-lg shadow-glow border ${selectedScans.has(scan.scan_id) ? 'border-mint-500 ring-1 ring-mint-500' : 'border-gray-200 dark:border-dark-600'} p-6 relative`}>
                    <div className="absolute top-4 right-4">
                      <input
                        type="checkbox"
                        checked={selectedScans.has(scan.scan_id)}
                        onChange={() => toggleScanSelection(scan.scan_id)}
                        className="rounded border-gray-300 text-mint-600 focus:ring-mint-500 h-5 w-5"
                      />
                    </div>
                    {/* Domain and Status */}
                    <div className="flex items-center justify-between mb-4 pr-8">
                      <div className="flex items-center">
                        <DocumentMagnifyingGlassIcon className="h-5 w-5 text-gray-400 mr-3" />
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {scan.domain?.domain_name || 'Unknown'}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center mb-4">
                      {getStatusIcon(scan.status)}
                      <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(scan.status)}`}>
                        {scan.status}
                      </span>
                    </div>

                    {/* Scan Details */}
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500 dark:text-gray-400">Type:</span>
                        <span className="text-sm text-gray-900 dark:text-white">{scan.scan_type}</span>
                      </div>

                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500 dark:text-gray-400">Started:</span>
                        <span className="text-sm text-gray-900 dark:text-white">
                          {formatDate(scan.started_at)}
                        </span>
                      </div>

                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500 dark:text-gray-400">Findings:</span>
                        <div>
                          {scan.findings_count > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {scan.high_risk_count > 0 && (
                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor('high')}`}>
                                  {scan.high_risk_count} high
                                </span>
                              )}
                              {scan.medium_risk_count > 0 && (
                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor('medium')}`}>
                                  {scan.medium_risk_count} medium
                                </span>
                              )}
                              {scan.low_risk_count > 0 && (
                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor('low')}`}>
                                  {scan.low_risk_count} low
                                </span>
                              )}
                              {scan.whitelisted_count > 0 && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-blue-600 bg-blue-100 dark:bg-blue-900/20 dark:text-blue-300">
                                  {scan.whitelisted_count} whitelisted
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              {scan.status === 'completed' ? 'No findings' : '-'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-end space-x-4 mt-4 pt-4 border-t border-gray-200 dark:border-dark-600">
                      {scan.status === 'completed' && (
                        <Link
                          to={`/reports/${scan.scan_id}`}
                          className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-mint-600 hover:text-mint-900 dark:text-mint-400 dark:hover:text-mint-300 bg-mint-50 dark:bg-mint-900/20 rounded-md"
                        >
                          <EyeIcon className="h-4 w-4 mr-1" />
                          View
                        </Link>
                      )}
                      <button
                        type="button"
                        onClick={() => handleDownloadLogs(scan.scan_id)}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 bg-blue-50 dark:bg-blue-900/20 rounded-md"
                      >
                        <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                        Logs
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteScan(scan.scan_id)}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 bg-red-50 dark:bg-red-900/20 rounded-md"
                      >
                        <TrashIcon className="h-4 w-4 mr-1" />
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              No scans in this period.
            </div>
          )}
        </div>
      )}
    </div>
  );

  if (loading) {

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark-900">
        <TGChibiVariants.Loading message="Loading your scans..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-900">
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="px-4 py-6 sm:px-0">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Scans</h1>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                Monitor and manage your typosquatting scans
              </p>
            </div>
            <button
              onClick={() => setShowCreateForm(true)}
              disabled={domains && domains.length === 0}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-mint-600 hover:bg-mint-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mint-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <PlayIcon className="-ml-1 mr-1.5 h-4 w-4" />
              New Scan
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
            <div className="text-sm text-red-700 dark:text-red-300">{error}</div>
          </div>
        )}

        {domains && domains.length === 0 && (
          <div className="mb-6 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-4">
            <div className="text-sm text-yellow-700 dark:text-yellow-300">
              You need to add and verify at least one domain before you can run scans.{' '}
              <Link to="/domains" className="font-medium underline">
                Add a domain now
              </Link>
            </div>
          </div>
        )}

        {/* Create Scan Form */}
        {showCreateForm && (
          <div className="mb-6 bg-white dark:bg-dark-800 shadow-glow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Create New Scan
            </h3>
            <form onSubmit={handleCreateScan} className="space-y-4">
              <div>
                <label htmlFor="domain" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Select Domain
                </label>
                <select
                  id="domain"
                  value={selectedDomain}
                  onChange={(e) => setSelectedDomain(e.target.value)}
                  className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white sm:text-sm"
                >
                  <option value="">Choose a domain to scan</option>
                  {domains.map((domain) => (
                    <option key={domain.id} value={domain.id}>
                      {domain.domain_name || domain.domain}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex space-x-3">
                <button
                  type="submit"
                  disabled={creatingScans || !selectedDomain}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-mint-600 hover:bg-mint-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mint-500 disabled:opacity-50"
                >
                  {creatingScans ? (
                    <TGChibi size="xs" animation="pulse" className="mr-2" />
                  ) : null}
                  {creatingScans ? 'Creating...' : 'Start Scan'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateForm(false);
                    setSelectedDomain('');
                    setError('');
                  }}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-dark-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-dark-700 hover:bg-gray-50 dark:hover:bg-dark-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mint-500"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Scans List with Groups */}
        <div className="mt-8 space-y-6">
          {!scans || scans.length === 0 ? (
            <div className="bg-white dark:bg-dark-800 shadow rounded-lg text-center py-12">
              <TGChibiVariants.Empty />
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No scans yet</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Start your first typosquatting scan to monitor for threats.
              </p>
              {domains && domains.length > 0 && (
                <div className="mt-6">
                  <button
                    onClick={() => setShowCreateForm(true)}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-mint-600 hover:bg-mint-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mint-500"
                  >
                    <PlayIcon className="-ml-1 mr-2 h-5 w-5" />
                    Start First Scan
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <AccordionSection
                title="Recent Scans (Last 7 Days)"
                scans={groupedScans.recent}
                expanded={expandedSections.recent}
                onToggle={() => toggleSection('recent')}
                count={groupedScans.recent.length}
              />

              <AccordionSection
                title="Older Scans (1 Week - 1 Month)"
                scans={groupedScans.older}
                expanded={expandedSections.older}
                onToggle={() => toggleSection('older')}
                count={groupedScans.older.length}
              />

              <AccordionSection
                title="Archive (Older than 1 Month)"
                scans={groupedScans.archive}
                expanded={expandedSections.archive}
                onToggle={() => toggleSection('archive')}
                count={groupedScans.archive.length}
              />
            </>
          )}
        </div>

        <BulkActionsBar />
      </div>
    </div>
  );
}
